import React, { useMemo, useEffect, useState } from 'react';
import Icon from '../../../components/AppIcon';
import DataTable from '../../../components/orizon-ui/DataTable';
import SearchInput from '../../../components/orizon-ui/SearchInput';
import StatusPill from '../../../components/orizon-ui/StatusPill';
import ExportMenu from '../../../components/orizon-ui/ExportMenu';
import ImpostazioniTabella, { FiltriBadge } from '../../../components/orizon-ui/ImpostazioniTabella';
import { useToast } from '../../../components/orizon-ui/Toast';
import { useListaGru } from '../../../components/gru/useListaGru';
import { formattaQta, formattaDataOra, formattaData } from '../../../components/gru/scorta';
import { esportaCSV, esportaXLSX, esportaPDF } from '../../../components/gru/esporta';
import { gruOpzioni, gruEsporta, mapGruError } from '../../../lib/services/gruService';
import FormIntervento from './FormIntervento';
import DettaglioIntervento, {
  tonoTipo, tonoStato, etichettaStato, descrizioneComponente,
} from './DettaglioIntervento';

const MAX_FILTRI = 5;

const STATI = [
  { value: 'registrato', label: 'Registrato' },
  { value: 'ordinato', label: 'In ordine' },
  { value: 'ricevuto', label: 'Ricevuto' },
  { value: 'annullato', label: 'Annullato' },
];

const COLONNE_VISIBILI = [
  { key: 'data_intervento', label: 'Data' },
  { key: 'tipo_label', label: 'Tipo' },
  { key: 'gru_codice', label: 'Gru' },
  { key: 'componente', label: 'Componente' },
  { key: 'nome', label: 'Articolo' },
  { key: 'qta', label: 'Q.tà' },
  { key: 'causa_label', label: 'Causa' },
  { key: 'stato', label: 'Stato' },
  { key: 'utente_nome', label: 'Utente' },
];

const TabellaInterventi = () => {
  const toast = useToast();

  const [tipi, setTipi] = useState([]);
  const [elencoGru, setElencoGru] = useState([]);
  const [reparti, setReparti] = useState([]);
  const [cause, setCause] = useState([]);

  const [impostazioniAperte, setImpostazioniAperte] = useState(false);
  const [colVisibili, setColVisibili] = useState({});
  const [densita, setDensita] = useState('normale');
  const [filtriUi, setFiltriUi] = useState([]);
  const [esportando, setEsportando] = useState(false);
  const [mostraAnnullati, setMostraAnnullati] = useState(false);

  const [formAperto, setFormAperto] = useState(false);
  const [selezionato, setSelezionato] = useState(null);

  const definizioniFiltri = useMemo(() => [
    { key: 'data', label: 'Data', tipo: 'data', db: (v) => {
      const [da, a] = String(v).split('|');
      return ['data_intervento', { da: da || undefined, a: a || undefined }];
    } },
    { key: 'tipo', label: 'Tipo', tipo: 'select', opzioni: tipi.map((t) => ({ value: t.id, label: t.label })), db: (v) => ['tipo_id', v] },
    { key: 'gru', label: 'Gru', tipo: 'select', opzioni: elencoGru.map((g) => ({ value: g.id, label: `${g.codice} — ${g.nome}` })), db: (v) => ['gru_id', v] },
    { key: 'reparto', label: 'Reparto', tipo: 'select', opzioni: reparti.map((r) => ({ value: r.label, label: r.label })), db: (v) => ['reparto_label', v] },
    { key: 'causa', label: 'Causa', tipo: 'select', opzioni: cause.map((c) => ({ value: c.id, label: c.label })), db: (v) => ['causa_id', v] },
    { key: 'stato', label: 'Stato', tipo: 'select', opzioni: STATI, db: (v) => ['stato', v] },
    { key: 'articolo', label: 'Articolo', tipo: 'testo', db: (v) => ['nome', { ilike: v }] },
    { key: 'ean', label: 'EAN', tipo: 'testo', db: (v) => ['ean', { ilike: v }] },
    { key: 'utente', label: 'Utente', tipo: 'testo', db: (v) => ['utente_nome', { ilike: v }] },
  ], [tipi, elencoGru, reparti, cause]);

  const filtriAttivi = useMemo(() => filtriUi.filter((f) => f.col && f.val), [filtriUi]);

  const filtri = useMemo(() => {
    const risultato = {};
    filtriAttivi.forEach((f) => {
      const def = definizioniFiltri.find((d) => d.key === f.col);
      if (!def) return;
      const [colonna, valore] = def.db(f.val);
      risultato[colonna] = valore;
    });
    // gli interventi annullati (eliminati / corretti) sono nascosti, salvo richiesta o filtro esplicito sullo stato
    if (!mostraAnnullati && risultato.stato === undefined) risultato.stato = { diverso: 'annullato' };
    return risultato;
  }, [filtriAttivi, definizioniFiltri, mostraAnnullati]);

  const lista = useListaGru({
    entita: 'interventi',
    filtri,
    ordineIniziale: { key: 'data_intervento', direction: 'desc' },
    perPagina: 25,
  });

  useEffect(() => {
    let annullato = false;
    Promise.all([gruOpzioni('tipi'), gruOpzioni('gru'), gruOpzioni('reparti'), gruOpzioni('cause')])
      .then(([t, g, r, c]) => {
        if (annullato) return;
        setTipi(t);
        setElencoGru(g);
        setReparti(r);
        setCause(c);
      })
      .catch((e) => {
        if (!annullato) toast.error(mapGruError(e).message);
      });
    return () => { annullato = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ─── Esportazione ──────────────────────────────────────────────────────────
  const COLONNE_EXPORT = [
    { label: 'Data', valore: (r) => formattaDataOra(r.data_intervento), larghezza: 18, align: 'left' },
    { label: 'Tipo', valore: (r) => r.tipo_label, larghezza: 14, align: 'center' },
    { label: 'Gru', valore: (r) => r.gru_codice, larghezza: 12, align: 'center' },
    { label: 'Componente', valore: (r) => descrizioneComponente(r) ?? '', larghezza: 28, align: 'left' },
    { label: 'EAN', valore: (r) => r.ean, larghezza: 16, align: 'left' },
    { label: 'Articolo', valore: (r) => r.nome, larghezza: 38, align: 'left' },
    { label: 'Q.tà', valore: (r) => Number(r.qta), larghezza: 10, align: 'center' },
    { label: 'Unità', valore: (r) => r.unita_label, larghezza: 8, align: 'center' },
    { label: 'Ricevuta', valore: (r) => (r.tipo_movimento === null ? Number(r.qta_ricevuta ?? 0) : ''), larghezza: 10, align: 'center' },
    { label: 'Residuo', valore: (r) => (r.tipo_movimento === null ? Number(r.qta_residua ?? 0) : ''), larghezza: 10, align: 'center' },
    { label: 'Causa', valore: (r) => r.causa_label ?? '', larghezza: 22, align: 'left' },
    { label: 'Stato', valore: (r) => etichettaStato(r.stato), larghezza: 12, align: 'center' },
    { label: 'Consegna prevista', valore: (r) => (r.data_consegna_prevista ? formattaData(r.data_consegna_prevista) : ''), larghezza: 16, align: 'center' },
    { label: 'Utente', valore: (r) => r.utente_nome, larghezza: 20, align: 'left' },
    { label: 'Nota', valore: (r) => r.note ?? '', larghezza: 40, align: 'left' },
  ];

  const descrizioneFiltri = () => {
    const parti = [];
    if (lista.ricercaInput.trim()) parti.push(`Ricerca: "${lista.ricercaInput.trim()}"`);
    filtriAttivi.forEach((f) => {
      const def = definizioniFiltri.find((d) => d.key === f.col);
      let valore = f.val;
      if (def?.tipo === 'select') valore = def.opzioni.find((o) => o.value === f.val)?.label ?? f.val;
      if (def?.tipo === 'data') {
        const [da, a] = String(f.val).split('|');
        valore = `${da ? formattaData(da) : '…'} → ${a ? formattaData(a) : '…'}`;
      }
      parti.push(`${def?.label ?? f.col}: ${valore}`);
    });
    return parti.length ? `Filtri — ${parti.join(' · ')}` : '';
  };

  const esporta = async (formato) => {
    setEsportando(true);
    try {
      const { righe, totale, troncato } = await gruEsporta('interventi', {
        sort: lista.sort,
        ricerca: lista.ricercaInput,
        filtri,
      });
      if (righe.length === 0) {
        toast.warn('Nessun intervento da esportare.');
        return;
      }
      const base = { nomeFile: 'interventi_gru', colonne: COLONNE_EXPORT, righe };
      if (formato === 'csv') esportaCSV(base);
      else if (formato === 'xlsx') await esportaXLSX({ ...base, nomeFoglio: 'Interventi GRU' });
      else await esportaPDF({ ...base, nomeFile: 'Interventi_GRU', titolo: 'INTERVENTI GRU', sottotitolo: descrizioneFiltri() });
      toast.success(`Esportati ${righe.length} interventi.`);
      if (troncato) toast.warn(`Esportazione limitata ai primi ${righe.length} su ${totale} interventi: restringi i filtri.`);
    } catch (e) {
      toast.error(mapGruError(e).message);
    } finally {
      setEsportando(false);
    }
  };

  // ─── Colonne ───────────────────────────────────────────────────────────────
  const tutteLeColonne = [
    {
      key: 'data_intervento', label: 'Data', width: 150,
      render: (r) => <span className="font-mono text-[12px] font-bold text-muted-foreground">{formattaDataOra(r.data_intervento)}</span>,
    },
    {
      key: 'tipo_label', label: 'Tipo', width: 140, align: 'center',
      render: (r) => (
        <div className="flex flex-col items-center gap-1">
          <StatusPill tono={tonoTipo(r)} label={r.tipo_label} />
          {r.correzione_di && <StatusPill tono="info" label="Corretto" />}
        </div>
      ),
    },
    {
      key: 'gru_codice', label: 'Gru', width: 110, align: 'center',
      render: (r) => <span className="text-[12px] font-mono font-bold text-primary/80">{r.gru_codice}</span>,
    },
    {
      key: 'componente', label: 'Componente', width: 220, noSort: true,
      render: (r) => <span className="text-[12px] font-bold text-muted-foreground">{descrizioneComponente(r) ?? '---'}</span>,
    },
    {
      key: 'nome', label: 'Articolo', width: 280,
      render: (r) => (
        <div className="flex flex-col gap-0.5">
          <span className="text-[13px] font-black uppercase tracking-tight text-foreground/75 group-hover:text-foreground transition-colors leading-tight">{r.nome}</span>
          <span className="font-mono text-[10px] font-bold text-muted-foreground/50">{r.ean}</span>
        </div>
      ),
    },
    {
      key: 'qta', label: 'Q.tà', width: 150, align: 'center',
      render: (r) => (
        <div className="flex flex-col items-center gap-0.5">
          <span className="text-[14px] font-black tabular-nums text-foreground/80">
            {formattaQta(r.qta, r.unita_decimali)} <span className="text-[9px] uppercase tracking-widest text-muted-foreground/40">{r.unita_label}</span>
          </span>
          {/* ordini: ordinata, ricevuta e residuo */}
          {r.tipo_movimento === null && r.stato !== 'annullato' && (
            <span className="text-[9px] font-black uppercase tracking-wider tabular-nums text-muted-foreground/60 whitespace-nowrap">
              ric. {formattaQta(r.qta_ricevuta, r.unita_decimali)} · res.{' '}
              <span className={Number(r.qta_residua) > 0 ? 'text-amber-500' : 'text-emerald-500'}>
                {formattaQta(r.qta_residua, r.unita_decimali)}
              </span>
            </span>
          )}
        </div>
      ),
    },
    {
      key: 'causa_label', label: 'Causa', width: 160,
      render: (r) => <span className="text-[12px] font-bold text-muted-foreground">{r.causa_label ?? '---'}</span>,
    },
    {
      key: 'stato', label: 'Stato', width: 130, align: 'center',
      render: (r) => <StatusPill tono={tonoStato(r.stato)} label={etichettaStato(r.stato)} pulse={r.stato === 'ordinato'} />,
    },
    {
      key: 'utente_nome', label: 'Utente', width: 160,
      render: (r) => <span className="text-[11px] font-black uppercase tracking-widest text-muted-foreground/60">{r.utente_nome}</span>,
    },
    {
      key: '__apri', label: '', width: 120, align: 'center', noSort: true,
      render: (r) => (
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); setSelezionato(r); }}
          className="inline-flex items-center justify-center gap-1.5 w-10 h-10 md:w-auto md:px-5 md:py-2 rounded-2xl bg-primary/5 border border-primary/20 text-primary/60 hover:bg-primary hover:text-primary-foreground hover:border-primary hover:scale-105 active:scale-95 transition-all duration-300 text-[10px] font-black uppercase tracking-[.15em]"
        >
          <Icon name="Eye" size={15} strokeWidth={3} />
          <span className="hidden md:inline">Apri</span>
        </button>
      ),
    },
  ];
  const colonne = tutteLeColonne.filter((c) => c.key === '__apri' || colVisibili[c.key] !== false);

  const rimuoviFiltro = (indice) => setFiltriUi((prec) => prec.filter((_, i) => i !== indice));

  return (
    <div className="w-full animate-in fade-in duration-700">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-end gap-4 mb-6 px-1">
        <div>
          <h2 className="text-4xl md:text-5xl font-black tracking-[-0.06em] uppercase leading-none text-foreground">
            INTERVENTI<span className="text-primary">GRU</span>
          </h2>
          <div className="flex items-center gap-2 mt-2 md:mt-3">
            <span className="h-[2px] w-6 bg-primary rounded-full" />
            <p className="text-[10px] font-black uppercase tracking-[.35em] text-muted-foreground/50">
              Sostituzioni, rientri e ordini
            </p>
          </div>
        </div>

        <button
          type="button"
          onClick={() => setFormAperto(true)}
          className="flex items-center gap-2 px-4 py-2.5 md:px-6 md:py-3 rounded-2xl bg-primary text-primary-foreground hover:bg-primary/90 transition-all shadow-lg shadow-primary/20 group active:scale-95"
        >
          <Icon name="Plus" size={18} className="group-hover:rotate-90 transition-transform duration-300" />
          <span className="text-[10px] font-black uppercase tracking-[.2em]">Nuovo intervento</span>
        </button>
      </div>

      <div className="flex flex-wrap items-center gap-2 md:gap-3 mb-4">
        <SearchInput value={lista.ricercaInput} onChange={lista.setRicercaInput} placeholder="Cerca articolo, EAN, gru..." onScansiona={lista.setRicercaInput} />

        <ExportMenu onEsporta={esporta} inCorso={esportando} disabilitato={lista.loading} />

        <button
          type="button"
          onClick={() => setImpostazioniAperte(true)}
          className={`relative flex items-center gap-2 px-3 md:px-4 py-2.5 border rounded-2xl text-[11px] font-black uppercase tracking-widest transition-all duration-200 shadow-sm ${
            filtriAttivi.length > 0
              ? 'bg-primary/10 border-primary/30 text-primary hover:bg-primary/20'
              : 'bg-muted/40 hover:bg-muted/80 border-border/40 hover:border-primary/30 text-muted-foreground hover:text-foreground'
          }`}
        >
          <Icon name={filtriAttivi.length > 0 ? 'Filter' : 'Settings2'} size={14} />
          <span className="hidden sm:inline">{filtriAttivi.length > 0 ? `Filtri (${filtriAttivi.length})` : 'Impostazioni'}</span>
          {filtriAttivi.length > 0 && (
            <span className="absolute -top-1.5 -right-1.5 w-4 h-4 rounded-full bg-primary text-primary-foreground text-[9px] font-black flex items-center justify-center">
              {filtriAttivi.length}
            </span>
          )}
        </button>

        <button
          type="button"
          onClick={() => setMostraAnnullati((v) => !v)}
          className={`flex items-center gap-2 px-3 md:px-4 py-2.5 border rounded-2xl text-[11px] font-black uppercase tracking-widest transition-all duration-200 shadow-sm ${
            mostraAnnullati
              ? 'bg-primary/10 border-primary/30 text-primary hover:bg-primary/20'
              : 'bg-muted/40 hover:bg-muted/80 border-border/40 hover:border-primary/30 text-muted-foreground hover:text-foreground'
          }`}
        >
          <Icon name={mostraAnnullati ? 'Eye' : 'EyeOff'} size={14} />
          <span className="hidden sm:inline">Annullati</span>
        </button>

        <button
          type="button"
          onClick={lista.ricarica}
          disabled={lista.loading}
          className="flex items-center gap-2 px-3 md:px-4 py-2.5 bg-primary/10 hover:bg-primary/20 border border-primary/30 rounded-2xl text-[11px] font-black uppercase tracking-widest text-primary transition-all duration-200 shadow-sm disabled:opacity-50"
        >
          <Icon name="RefreshCw" size={14} className={lista.loading ? 'animate-spin' : ''} />
          <span className="hidden sm:inline">Aggiorna</span>
        </button>
      </div>

      {filtriAttivi.length > 0 && (
        <div className="mb-3">
          <FiltriBadge filtri={filtriUi} definizioni={definizioniFiltri} onRimuovi={rimuoviFiltro} />
        </div>
      )}

      <DataTable
        colonne={colonne}
        righe={lista.righe}
        totale={lista.totale}
        loading={lista.loading}
        pagina={lista.pagina}
        perPagina={lista.perPagina}
        onPagina={lista.setPagina}
        sort={lista.sort}
        onSort={lista.onSort}
        densita={densita}
        onRigaClick={setSelezionato}
        rigaDisattivata={(r) => r.stato === 'annullato'}
        messaggioVuoto={lista.ricercaInput || filtriAttivi.length ? 'Nessun intervento corrisponde alla ricerca' : 'Nessun intervento registrato'}
        altezzaMax="max-h-[65dvh]"
      />

      {impostazioniAperte && (
        <ImpostazioniTabella
          colonne={COLONNE_VISIBILI}
          colVisibili={colVisibili}
          setColVisibili={setColVisibili}
          densita={densita}
          setDensita={setDensita}
          filtri={filtriUi}
          setFiltri={setFiltriUi}
          definizioniFiltri={definizioniFiltri}
          maxFiltri={MAX_FILTRI}
          onClose={() => setImpostazioniAperte(false)}
        />
      )}

      {formAperto && (
        <FormIntervento
          onClose={() => setFormAperto(false)}
          onSaved={() => {
            setFormAperto(false);
            lista.ricarica();
          }}
        />
      )}

      {selezionato && (
        <DettaglioIntervento
          intervento={selezionato}
          onClose={() => setSelezionato(null)}
          onAggiornato={lista.ricarica}
        />
      )}
    </div>
  );
};

export default TabellaInterventi;
